import React, { useState, useRef, useEffect } from 'react';
import { Info, X, ChevronDown, Search, Menu, Trash2, ChevronsUpDown } from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import Tooltip from '../addentities/Tooltip.js';
import { ReactSortable } from 'react-sortablejs';
import { createPortal } from 'react-dom';
import WorkInProgress from '../../components/WorkInProgress.js';
import TestSyncModal from '../../components/TestSyncModal.js';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import StockSearchDropdown from '../../components/StockSearchDropdown.js';
import './style.css';
import apiHelper from '../../apiHelper.js';
import AlertNotification from '../../components/AlertNotification.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';

const portalRoot = typeof document !== 'undefined' ? document.getElementById('portal-root') : null;
const OptionPortal = ({ children, style }) => {
    if (portalRoot) {
        return createPortal(
            <div className="options-container w-full bg-white border rounded shadow-lg" style={{ ...style, overflow: 'auto', zIndex: 2000 }}>
                {children}
            </div>,
            portalRoot
        );
    }

    return (
        <div className="options-container w-full bg-white border rounded shadow-lg" style={{ ...style, overflow: 'auto', zIndex: 2000 }}>
            {children}
        </div>
    );
};

// Success SVG icon component (using your exact SVG)
const SuccessIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px' }}>
        <path d="M7.99967 1.3335C4.32367 1.3335 1.33301 4.32416 1.33301 8.00016C1.33301 11.6762 4.32367 14.6668 7.99967 14.6668C11.6757 14.6668 14.6663 11.6762 14.6663 8.00016C14.6663 4.32416 11.6757 1.3335 7.99967 1.3335ZM7.11631 10.4929C6.86809 10.7411 6.46571 10.7413 6.21722 10.4933L4.66331 8.94278C4.40281 8.68284 4.40228 8.26096 4.66213 8.00037C4.92215 7.73962 5.34435 7.73914 5.60496 7.99929L6.66567 9.05816L9.72367 6.00016C9.98398 5.73985 10.406 5.73985 10.6663 6.00016C10.9267 6.26047 10.9267 6.68252 10.6663 6.94283L7.11631 10.4929Z" fill="#146C43" />
    </svg>
);

// Failed SVG icon component (using your exact SVG)
const FailedIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px' }}>
        <path d="M7.96834 1.3335C4.30967 1.3335 1.33301 4.32416 1.33301 8.00016C1.33301 11.6762 4.32367 14.6668 7.99967 14.6668C11.6757 14.6668 14.6663 11.6762 14.6663 8.00016C14.6663 4.32416 11.6617 1.3335 7.96834 1.3335ZM8.66634 10.6668C8.66634 11.035 8.36786 11.3335 7.99967 11.3335C7.63148 11.3335 7.33301 11.035 7.33301 10.6668C7.33301 10.2986 7.63148 10.0002 7.99967 10.0002C8.36786 10.0002 8.66634 10.2986 8.66634 10.6668ZM8.66634 8.00016C8.66634 8.36835 8.36786 8.66683 7.99967 8.66683C7.63148 8.66683 7.33301 8.36835 7.33301 8.00016V5.3335C7.33301 4.96531 7.63148 4.66683 7.99967 4.66683C8.36786 4.66683 8.66634 4.96531 8.66634 5.3335V8.00016Z" fill="#DC3545" />
    </svg>
);

export default function AddListing() {
    const history = useHistory();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [isSaving, setIsSaving] = useState(false); // Track if we're saving
    const [listings, setListings] = useState([]);

    const breadcrumbs = ["Listing", "Platform Listing", "Add Listing"];
    const [selectStockError, setSelectStockError] = useState("");
    const [isDirty, setIsDirty] = useState(false);
    
    // Discard modal state - using same pattern as EditListing
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);
    const [nextLocation, setNextLocation] = useState(null);

    // Platform mapping rows
    const [rows, set_rows] = useState([]);
const [rowsUpdateCheck, setRowsUpdateCheck] = useState(0);
    const [enableTestSync, setEnableTestSync] = useState(false);
    const [showTestSyncModal, setShowTestSyncModal] = useState(false);
    const row_refs = useRef({});
    const location = useLocation();
    const [duplicate_stock_error, set_duplicate_stock_error] = useState(false);

    // Alert state
    const [showItemAlert, setShowItemAlert] = useState(false);
    const [alert, setAlert] = useState({
        type: "",
        action: "",
        recordName: "",
        message: "",
    });

    // New states for duplicate validation and save confirmation
    const [duplicateErrors, setDuplicateErrors] = useState({ general: {}, fields: {} });
    const [saveWithErrorsModal, setSaveWithErrorsModal] = useState(false);
    
    // New state for ID field validation
    const [idFieldError, setIdFieldError] = useState("");

    // Track which rows have been tested via Test Sync
    const [testedRows, setTestedRows] = useState(new Set());

    // State for showing empty field errors only on save attempt
    const [showEmptyFieldErrors, setShowEmptyFieldErrors] = useState(false);

    // Track the last modified row ID
    const [lastModifiedRowId, setLastModifiedRowId] = useState(null);

    // 🧭 Block in-app navigation - Same pattern as EditListing
    useEffect(() => {
        if (!isDirty || isDiscarding || isSaving) return;

        const unblock = history.block((tx) => {
            setNextLocation(tx);
            setDiscardModalOpen(true);
            return false;
        });

        return () => unblock();
    }, [isDirty, isDiscarding, isSaving, history]);

    // 🧱 Warn on browser/tab close
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty && !isDiscarding && !isSaving) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isDirty, isDiscarding, isSaving]);

    // Initialize with one empty row
    useEffect(() => {
        if (rows.length === 0) {
            set_rows([{ id: 1, platform: '', product_id: '', sku_id: '', platform_open: false }]);

        }
    }, []);

    const add_row = () => {
        const newId = rows.length ? Math.max(...rows.map(r => r.id)) + 1 : 1;
        set_rows(prev => [...prev, { id: newId, platform: '', product_id: '', sku_id: '', platform_open: false, isNew: true }]);
        setLastModifiedRowId(newId); // Track the newly added row
        setIsDirty(true);
    };
    
    // Reusable duplicate validation — runs against a given rows array
    const validateDuplicates = (rowsToCheck) => {
        const generalErrors = {};
        const fieldErrors = {};
        for (let i = 0; i < rowsToCheck.length; i++) {
            const row1 = rowsToCheck[i];
            if (!row1.platform) continue;
            for (let j = i + 1; j < rowsToCheck.length; j++) {
                const row2 = rowsToCheck[j];
                if (!row2.platform) continue;
                if (row1.platform === row2.platform) {
                    const productIdMatch = row1.product_id && row2.product_id && row1.product_id === row2.product_id;
                    const skuIdMatch = row1.sku_id && row2.sku_id && row1.sku_id === row2.sku_id;
                    if (productIdMatch || skuIdMatch) {
                        generalErrors[row1.id] = "Duplicate ID found";
                        generalErrors[row2.id] = "Duplicate ID found";
                        if (!fieldErrors[row1.id]) fieldErrors[row1.id] = { productId: false, skuId: false };
                        if (!fieldErrors[row2.id]) fieldErrors[row2.id] = { productId: false, skuId: false };
                        if (productIdMatch) { fieldErrors[row1.id].productId = true; fieldErrors[row2.id].productId = true; }
                        if (skuIdMatch) { fieldErrors[row1.id].skuId = true; fieldErrors[row2.id].skuId = true; }
                    }
                }
            }
        }
        return { general: generalErrors, fields: fieldErrors };
    };

    const delete_row = id => {
        // Remove row and re-validate duplicates on the remaining rows
        set_rows(prev => {
            const newRows = prev.filter(r => r.id !== id);
            setDuplicateErrors(validateDuplicates(newRows));
            return newRows;
        });
        // Remove from tested rows set
        setTestedRows(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
        setIsDirty(true);
    };
    
const update_row = (id, changes) => {
    // Track the last modified row
    setLastModifiedRowId(id);
    console.log('Last modified row:', id);
    
    set_rows(prev => {
        const updated = prev.map(r => {
            if (r.id === id) {
                return { ...r, ...changes };
            }
            return r;
        });
        
        // Validate duplicates - now track which field caused the duplicate
        const generalErrors = {};
        const fieldErrors = {}; // Track field-specific errors { rowId: { productId: boolean, skuId: boolean } }
        
        // Check each row against all other rows
        for (let i = 0; i < updated.length; i++) {
            const row1 = updated[i];
            
            // Skip if row doesn't have platform
            if (!row1.platform) continue;
            
            for (let j = i + 1; j < updated.length; j++) {
                const row2 = updated[j];
                
                // Skip if row doesn't have platform
                if (!row2.platform) continue;
                
                // Check if they have the same platform
                if (row1.platform === row2.platform) {
                    
                    // Check if product_id matches
                    const productIdMatch = row1.product_id && row2.product_id && row1.product_id === row2.product_id;
                    
                    // Check if sku_id matches
                    const skuIdMatch = row1.sku_id && row2.sku_id && row1.sku_id === row2.sku_id;
                    
                    if (productIdMatch || skuIdMatch) {
                        console.log('Duplicate found between row', row1.id, 'and row', row2.id);
                        
                        // Set general error message for the row
                        generalErrors[row1.id] = "Duplicate ID found";
                        generalErrors[row2.id] = "Duplicate ID found";
                        
                        // Track field-specific errors
                        if (!fieldErrors[row1.id]) fieldErrors[row1.id] = { productId: false, skuId: false };
                        if (!fieldErrors[row2.id]) fieldErrors[row2.id] = { productId: false, skuId: false };
                        
                        if (productIdMatch) {
                            fieldErrors[row1.id].productId = true;
                            fieldErrors[row2.id].productId = true;
                        }
                        
                        if (skuIdMatch) {
                            fieldErrors[row1.id].skuId = true;
                            fieldErrors[row2.id].skuId = true;
                        }
                    }
                }
            }
        }
        
        console.log('Final duplicate errors:', generalErrors);
        console.log('Field-specific errors:', fieldErrors);
        setDuplicateErrors({ 
            general: generalErrors, 
            fields: fieldErrors 
        });
        
        return updated;
    });
    
    // If platform, product_id, or sku_id changes, remove from tested rows
    if (changes.platform !== undefined || changes.product_id !== undefined || changes.sku_id !== undefined) {
        setTestedRows(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    }
    
    setIsDirty(true);
};

    const save_ref2 = useRef(null);
    const [save_modal_open_2, set_save_modal_open_2] = useState(false);
    const handle_save_confirm_2 = () => set_save_modal_open_2(false);
    useEffect(() => {
        function outside(e) { if (save_ref2.current && !save_ref2.current.contains(e.target)) set_save_modal_open_2(false); }
        if (save_modal_open_2) document.addEventListener('mousedown', outside);
        return () => document.removeEventListener('mousedown', outside);
    }, [save_modal_open_2]);

    const [platformOptions, setPlatformOptions] = useState([]);
    const [form_data, set_form_data] = useState({
        select_stock: null,
        id: "",
        status: "1",
        notes: "",
    });

    useEffect(() => {
        const fetchListings = async () => {
            try {
                const response = await apiHelper("sales/listings/", "GET");
                if (!response.ok) throw new Error("Failed to fetch listings");
                const data = await response.json();
                setListings(data);
            } catch (error) {
                console.error(error);
            }
        };
        const loadAll = async () => {
            try {
                await fetchListings();
            } catch (e) {
                console.error(e);
            }
        };
        loadAll();
    }, []);

    useEffect(() => {
        async function fetchPlatforms() {
            setLoading(true);
            setError(null);

            try {
                const response = await apiHelper(
                    "sales/platforms/",
                    "GET"
                );

                const apiData = await response.json();
                console.log("Platforms API Response:", apiData);

                if (!Array.isArray(apiData)) {
                    setPlatformOptions([]);
                    return;
                }

                const mapped = apiData.map(item => item.platform_name);
                setPlatformOptions(mapped);

            } catch (err) {
                console.error(err);
                setError("Failed to load platforms");
            } finally {
                setLoading(false);
            }
        }

        fetchPlatforms();
    }, []);

const handle_select_stock_select = (opt) => {
    set_form_data((prev) => ({ ...prev, select_stock: opt }));
    if (opt.listed) {
        setSelectStockError("This Stock is already listed. Please select another");
    } else if (opt.status === "Inactive") {
        setSelectStockError("Stock does not exist. Please select another");
    } else {
        setSelectStockError("");
    }
    setIsDirty(true);
};

const handleStockBlur = () => {
    if (!form_data.select_stock) {
        setSelectStockError("Missing required information");
        return;
    }
    if (form_data.select_stock?.listed) {
        setSelectStockError("This Stock is already listed. Please select another");
        return;
    }
    if (form_data.select_stock?.status === "Inactive") {
        setSelectStockError("Stock does not exist. Please select another");
        return;
    }
    setSelectStockError("");
};


    useEffect(() => {
        console.log('location.state on mount:', location.state);

        if (location && location.state && location.state.success) {
            setSuccessMessage(location.state.success);
            history.replace(location.pathname, {});

            const t = setTimeout(() => setSuccessMessage(''), 5000);
            return () => clearTimeout(t);
        }
    }, [location, history]);

    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(""), 5000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    // Check if Test Sync button should be enabled - Updated to NOT disable on test sync failure
    useEffect(() => {
        const hasValidRows = rows.some(r => r.platform && r.product_id && r.sku_id);
        // Remove the check for hasError from the condition
        const hasNoBlockingErrors = !rows.some(r => {
            // Only check for empty fields and duplicate errors, NOT test sync errors
            const hasEmptyFieldError = !r.platform || !r.product_id || !r.sku_id || 
                                      r.platform.trim() === '' || 
                                      r.product_id.trim() === '' || 
                                      r.sku_id.trim() === '';
            return duplicateErrors.general?.[r.id] || hasEmptyFieldError;
        });
        setEnableTestSync(hasValidRows && hasNoBlockingErrors);
    }, [rows, rowsUpdateCheck, duplicateErrors]);

    // Function to check for empty rows
    const hasEmptyRows = () => {
        return rows.some(row => {
            const isEmpty = !row.platform || !row.product_id || !row.sku_id || 
                           row.platform.trim() === '' || 
                           row.product_id.trim() === '' || 
                           row.sku_id.trim() === '';
            return isEmpty;
        });
    };


    // Close any open platform portals when clicking outside
    useEffect(() => {
        const handler = (e) => {
            const clickedInPortal = portalRoot && portalRoot.contains(e.target);
            set_rows(prev => prev.map(row => {
                const el = row_refs.current[row.id]?.current;
                if (!clickedInPortal && el && !el.contains(e.target)) {
                    return { ...row, platform_open: false };
                }
                return row;
            }));
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Check if there are any errors in rows
    const hasRowErrors = () => {
        return rows.some(row => {
            const hasEmptyFieldError = !row.platform || !row.product_id || !row.sku_id || 
                                      row.platform.trim() === '' || 
                                      row.product_id.trim() === '' || 
                                      row.sku_id.trim() === '';
            return row.hasError || duplicateErrors.general?.[row.id] || hasEmptyFieldError;
        });
    };
    
    // Check if there are any errors in Stock Info section
    const hasStockInfoErrors = () => {
        return selectStockError !== "" || idFieldError !== "";
    };

const handle_save = async () => {
    setIsSaving(true);
    
    // Clear isNew from all rows so they all validate on save
    set_rows(prev => prev.map(r => ({ ...r, isNew: false })));

    // Set flag to show empty field errors
    setShowEmptyFieldErrors(true);
    
    // Check for duplicate errors first - BLOCK SAVING
    console.log('Checking duplicate errors on save:', duplicateErrors.general);
    console.log('Duplicate errors keys:', Object.keys(duplicateErrors.general || {}));
    
    if (duplicateErrors.general && Object.keys(duplicateErrors.general).length > 0) {
        console.log('Blocking save due to duplicate errors');
        setAlert({
            type: "error",
            action: "create",
            recordName: "Listing",
            message: "Error Saving"
        });
        setShowItemAlert(true);
        setIsSaving(false);
        return;
    }
    
    // First validate all rows for empty fields
    const emptyRowsExist = hasEmptyRows();
    if (emptyRowsExist) {
        console.log("Empty rows detected, triggering validation");
        
        // Show error toast
        setAlert({
            type: "error",
            action: "create",
            recordName: "Listing",
            message: "Please fill in all required fields in Platform Mapping"
        });
        setShowItemAlert(true);
        setIsSaving(false);
        return;
    }
    
    if (hasStockInfoErrors()) {
        setAlert({
            type: "error",
            action: "create",
            recordName: "Listing",
            message: "Error Saving"
        });
        setShowItemAlert(true);
        setIsSaving(false);
        return;
    }

    if (!form_data.select_stock) {
        setSelectStockError("Missing required information");
        setIsSaving(false);
        return;
    } else if (form_data.select_stock?.listed) {
        setSelectStockError("This Stock is already listed. Please select another");
        setIsSaving(false);
        return;
    } else if (form_data.select_stock?.status === "Inactive") {
        setSelectStockError("Stock does not exist. Please select another");
        setIsSaving(false);
        return;
    } else {
        setSelectStockError("");
    }

    const hasErrors = hasRowErrors();

    if (hasErrors) {
        setSaveWithErrorsModal(true);
        setIsSaving(false);
        return;
    }

    // Reset showEmptyFieldErrors before saving
    setShowEmptyFieldErrors(false);
    
    await saveListing(false);
};
    // Function to handle saving (refactored from original handle_save)
    const saveListing = async (hasErrors = false) => {
        try {
            // Prepare platform mappings with wasTested flag
            const platformMappings = rows
                .filter(row => row.platform && row.product_id && row.sku_id)
                .map((row, index) => {
                    // Parse price safely
                    let platformPrice = null;
                    if (row.product_price && row.product_price !== '-' && row.product_price !== 'Invalid ID') {
                        try {
                            const priceString = row.product_price.replace(/[^\d.-]/g, '');
                            const parsed = parseFloat(priceString);
                            platformPrice = isNaN(parsed) ? null : parsed;
                        } catch (error) {
                            console.error('Error parsing price:', error);
                            platformPrice = null;
                        }
                    }

                    // Parse stock quantity safely
                    let platformStockQuantity = 0;
                    if (row.prioduct_stock && row.prioduct_stock !== '-' && row.prioduct_stock !== 'Invalid ID') {
                        try {
                            const stockString = row.prioduct_stock.replace(/[^\d]/g, '');
                            const parsed = parseInt(stockString, 10);
                            platformStockQuantity = isNaN(parsed) ? 0 : parsed;
                        } catch (error) {
                            console.error('Error parsing stock:', error);
                            platformStockQuantity = 0;
                        }
                    }

                    return {
                        platform: row.platform,
                        product_id: row.product_id,
                        sku_id: row.sku_id,
                        platform_product_name: row.product_name || '',
                        platform_sku_name: row.sku_name || '',
                        platform_price: platformPrice,
                        platform_stock_quantity: platformStockQuantity,
                        test_status: row.hasError ? 'failed' : (row.verified ? 'success' : 'pending'),
                        was_tested: testedRows.has(row.id) // Add flag to indicate if this row was tested via Test Sync
                    };
                });

            // Single API call payload
            const payload = {
                stock_id: form_data.select_stock.id,
                listing_code: form_data.id || null,
                status: form_data.status === "1",
                notes: form_data.notes || '',
                platform_mappings: platformMappings
            };

            console.log("Creating listing with platform mappings:", payload);

            // Use the new single API endpoint
            const response = await apiHelper(
                "sales/listings/create-with-mappings/",  // New endpoint
                "POST",
                payload
            );

            const result = await response.json();
            console.log("API Response:", result);

            if (!response.ok) {
                setAlert({
                    type: "error",
                    action: "create",
                    recordName: "Listing",
                    message: result.error || "Failed to create listing"
                });
                setShowItemAlert(true);
                setIsSaving(false); // Reset saving flag
                return;
            }

            // Success - clear dirty state and redirect with appropriate message
            setIsDirty(false);
            
            if (hasErrors) {
                history.push("/listing-pricing", {
                    success: "Listing saved with Error",
                    type: "warning"
                });
            } else {
                history.push("/listing-pricing", {
                    success: "Listing Saved Successfully",
                    type: "success"
                });
            }

        } catch (error) {
            console.error("Error in saveListing:", error);
            setAlert({
                type: "error",
                action: "create",
                recordName: "Listing",
                message: error.message || "An unexpected error occurred while saving"
            });
            setShowItemAlert(true);
            setIsSaving(false); // Reset saving flag
        } finally {
            setIsSaving(false); // Ensure saving flag is reset
        }
    };

    // Validate ID field
    const validateIdField = () => {
        if (form_data.id && form_data.id.length > 50) {
            setIdFieldError(" ");
            return false;
        }
        if(listings.find(l => l.listing_code == form_data.id)) {
            setIdFieldError("Listing ID already exists");
            return false;
        }
        setIdFieldError("");
        return true;
    };
    
    // Handle ID field change
    const handleIdChange = (e) => {
        const value = e.target.value;
        set_form_data({ ...form_data, id: value });
        setIsDirty(true);
        
        // Validate on change if there's an error
        if (idFieldError) {
            validateIdField();
        }
    };
    
    // Handle ID field blur
    const handleIdBlur = () => {
        validateIdField();
    };

    // Handle cancel button click
    const handleCancel = () => {
        if (isDirty && !isSaving) {
            setDiscardModalOpen(true);
        } else {
            history.push("/listing-pricing");
        }
    };

    // Handle discard confirmation - Same pattern as EditListing
    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            console.log("🟠 Discard confirmed, navigating...");
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardModalOpen(false);

            setTimeout(() => {
                console.log("🟢 Navigating with success state");
                
                // Navigate to the next location if we have one, otherwise go to listing-pricing
                if (nextLocation) {
                    const { pathname, state } = nextLocation.location || nextLocation;
                    const unblock = history.block(() => null);
                    
                    // Preserve the section state in navigation to update sidebar
                    history.push(pathname, { 
                        ...state,
                        section: state?.section || localStorage.getItem('selectedModule') || "Sales",
                        success: "Listing Discarded"
                    });
                    
                    setTimeout(() => unblock(), 0);
                    setNextLocation(null);
                } else {
                    // Default navigation if no next location
                    history.push("/listing-pricing", { 
                        success: "Listing Discarded",
                        section: "Sales"
                    });
                }
            }, 100);
        } else {
            setDiscardModalOpen(false);
            setNextLocation(null);
        }
    };

    return (
        <div className="company-body main-company-body-inner new-look">
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    console.log("crumn",crumb)
                    return (
                        <span
                            key={index}
                            className={`breadcrumb-item ${crumb === "Platform Listing" ? "clickable" : ""}`}
                            onClick={() => { if (crumb === "Platform Listing") history.push('/listing-pricing'); }}
                            style={{ cursor: crumb === "Platform Listing" ? 'pointer' : 'default' }}
                        >
                            {crumb}{!isLast ? '  ' : ''}
                        </span>
                    );
                })}
            </div>

            {showItemAlert && alert && (
                <AlertNotification
                    type={alert.type}
                    action={alert.action}
                    recordName={alert.recordName}
                    message={alert.message}
                    onClose={() => setShowItemAlert(false)}
                />
            )}

            {successMessage && (
                <AlertNotification
                    type={location.state?.type || "success"}
                    action="saved"
                    recordName="Listing"
                    message={successMessage}
                    onClose={() => setSuccessMessage('')}
                />
            )}

            {/* Discard Confirmation Modal - Using the reusable component */}
            <DiscardConfirmationModal
                isOpen={discardModalOpen}
                onCancel={() => {
                    setDiscardModalOpen(false);
                    setNextLocation(null);
                }}
                onConfirm={handleDiscardConfirm}
                entityName="Listing"
            />

            <div className='edit-organisation-info'>
                <div className='edit-organisation-left'>
                    <h1>Add Listing 
                        {/* <Tooltip content="Add Listing" title="Add Listing" body="Add Listing"><Info size={16} className="info-icon" /></Tooltip> */}
                        </h1>
                </div>
                <div className='discard-save-organisation-right d-flex align-items-center justify-content-end flex-wrap gap-2'>
                    <button className="dark-button" type="button" onClick={handleCancel}> Cancel </button>

                    <TestSyncModal
                        isOpen={showTestSyncModal}
                        onClose={() => setShowTestSyncModal(false)}
                        onConfirm={() => {
                            // When Test Sync is confirmed, mark all rows with platform, product_id, sku_id as tested
                            setTestedRows(prev => {
                                const newSet = new Set(prev);
                                rows.forEach(row => {
                                    if (row.platform && row.product_id && row.sku_id) {
                                        newSet.add(row.id);
                                    }
                                });
                                return newSet;
                            });
                            setShowTestSyncModal(false);
                        }}
                        rows={rows}
                        setRows={set_rows}
                        selectedStock={form_data.select_stock} 
                    />
                    <button className="dark-border" type="button" onClick={handle_save}>Save & Exit</button>
                </div>
            </div>

            <div className="employee-tabs-container">
                {save_modal_open_2 && (
                    <>
                        <div className="drawer-overlay" />
                        <div className="entity-drawer modal-popup-cls" ref={save_ref2} style={{ maxWidth: 650, margin: '0 auto', background: 'white', borderRadius: 12, zIndex: 1050, padding: 24, position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', boxShadow: '0px 2px 10px rgba(0,0,0,0.2)', height: 'auto', right: 'auto', overflow: 'inherit' }}>
                            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                <button className="close-btn" onClick={() => set_save_modal_open_2(false)}><X size={20} /></button>
                            </div>
                            <h3 style={{ textAlign: 'left', color: '#212529', fontSize: '20px', fontWeight: '600', lineHeight: '24px', fontFamily: 'Montserrat', marginBottom: '5px' }}>The edit function implementation</h3>
                            <div className="drawer-footer" style={{ paddingTop: '10px' }}>
                                <button className="btn-save" onClick={handle_save_confirm_2}>OK</button>
                            </div>
                        </div>
                    </>
                )}

                {/* Save with errors confirmation modal */}
                {saveWithErrorsModal && (
                    <>
                        <div className="drawer-overlay" />
                        <div className="entity-drawer modal-popup-cls" style={{ 
                            minWidth: "650px", 
                            margin: '0 auto', 
                            background: 'white', 
                            borderRadius: 12, 
                            zIndex: 1050, 
                            padding: 24, 
                            position: 'fixed', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)', 
                            boxShadow: '0px 2px 10px rgba(0,0,0,0.2)', 
                            height: 'auto', 
                            right: 'auto', 
                            overflow: 'inherit' 
                        }}>
                            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                <button className="close-btn" onClick={() => setSaveWithErrorsModal(false)}>
                                    <X size={20} />
                                </button>
                            </div>
                            <h3 style={{ 
                                textAlign: 'left', 
                                color: '#212529', 
                                fontSize: '20px', 
                                fontWeight: '600', 
                                lineHeight: '24px', 
                                fontFamily: 'Montserrat', 
                                marginBottom: '15px' 
                            }}>
                                Save Listing With Errors?
                            </h3>
                            <p style={{ 
                                color:  "#6C757D",
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: "18px", 
                            }}>
                                Listings with invalid Product ID or SKU ID can’t be mapped, so actions that require syncing to e-commerce platforms such as pricing updates won’t work
                            </p>
                            <div className="drawer-footer" style={{ 
                                paddingTop: '10px', 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                gap: '10px' 
                            }}>
                                <button 
                                    className="dark-button" 
                                    onClick={() => setSaveWithErrorsModal(false)}
                                    style={{
              display: "flex",
              height: "34px",
              minWidth: "100px",
              padding: "10px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              borderRadius: "8px",
              border: "1px solid  var(--navbar-dropdown-active, #212529)",
              background: "var(--Background-White, #FFF)",
              boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
              color: "var(--navbar-dropdown-active, #212529)",
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontWeight: 600,
              fontStyle: "normal",
              lineHeight: "normal",
            }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn-save" 
                                    onClick={async () => {
                                        setIsSaving(true); // Set saving flag
                                        setSaveWithErrorsModal(false);
                                        await saveListing(true); // true = has errors
                                    }}
                                    style={{
              display: "flex",
              height: "34px",
              minWidth: "100px",
              padding: "10px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              borderRadius: "8px",
              background: "var(--navbar-dropdown-active, #212529)",
              boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
              color: "var(--Background-White, #FFF)",
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontWeight: 600,
              fontStyle: "normal",
              lineHeight: "normal",
            }}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </>
                )}

                <div className="tab-content" id="edit-spu-content">
                    <div className="info-section">
                        {/* SPU / Stock INFO CARD */}
                        <div className="info-card new-info-overflow-auto">
                            <div className="info-header"><h4 className="info-title">Stock Info</h4></div>
                            <div className="info-content d-block">
                                <div className='grid-info-adding'>

                                    {/* ROW: Select Stock & Status */}
                                    <div className='row gappingcls-added'>
                                        <div className="col-lg-6 col-md-6 col-12">
    <div className="info-inner">
        <label htmlFor="select_stock" className="form-label d-flex align-items-center gap-1">
            Select Stock<span style={{ color: '#DC3545' }}>*</span>
            <Tooltip title="Select Stock" body="Select a Stock for Listing and Platform Mapping"><Info size={16} className="info-icon" /></Tooltip>
        </label>

        <StockSearchDropdown
            selected={form_data.select_stock}
            onSelect={handle_select_stock_select}
            onClear={() => {
                set_form_data((prev) => ({ ...prev, select_stock: null }));
                setSelectStockError("");
                setIsDirty(true);
            }}
            onClose={handleStockBlur}
            error={selectStockError}
        />
        {selectStockError && (
            <div className="invalid-feedback d-block" style={{ marginTop: "4px", color: "#DC3545", fontSize: "12px", display: "block" }}>
                {selectStockError}
            </div>
        )}
    </div>
</div>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label htmlFor="status_active" className="form-label">Status <span style={{ color: '#DC3545' }}>*</span></label>
                                                <div className='flexing-type-radio'>
                                                    <div className="form-check">
                                                        <input 
                                                            className="form-check-input" 
                                                            type="radio" 
                                                            id="status_active" 
                                                            name="status" 
                                                            value="1" 
                                                            checked={form_data.status === '1'} 
                                                            onChange={(e) => {
                                                                set_form_data({ ...form_data, status: e.target.value });
                                                                setIsDirty(true);
                                                            }} 
                                                        />
                                                        <label className="form-check-label" htmlFor="status_active" style={{ fontWeight: 'normal', fontSize: '14px' }}>Active</label>
                                                    </div>
                                                    <div className="form-check">
                                                        <input 
                                                            className="form-check-input" 
                                                            type="radio" 
                                                            id="status_inactive" 
                                                            name="status" 
                                                            value="0" 
                                                            checked={form_data.status === '0'} 
                                                            onChange={(e) => {
                                                                set_form_data({ ...form_data, status: e.target.value });
                                                                setIsDirty(true);
                                                            }} 
                                                        />
                                                        <label className="form-check-label" htmlFor="status_inactive" style={{ fontWeight: 'normal', fontSize: '14px' }}>Inactive</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* ROW: ID Field */}
                                    <div className='row gappingcls-added'>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label htmlFor="id" className="form-label d-flex align-items-center gap-1">ID
                                                </label>
                                                <style>{`
                                                    .add-listing-id-field input.form-control {
                                                        border: 1px solid #6C757D !important;
                                                    }
                                                    .add-listing-id-field.has-error input.form-control,
                                                    .add-listing-id-field.has-error input.form-control.is-invalid {
                                                        border: 1px solid #DC3545 !important;
                                                    }
                                                    .add-listing-id-field.has-error .character-counter {
                                                        color: #DC3545 !important;
                                                    }
                                                    .add-listing-id-field.has-error span {
                                                        color: #DC3545 !important;
                                                    }
                                                `}</style>
                                                <div className={`add-listing-id-field${idFieldError ? ' has-error' : ''}`}>
                                                    <CharacterLimitedTextarea
                                                        id="id"
                                                        name="id"
                                                        value={form_data.id}
                                                        onChange={handleIdChange}
                                                        onBlur={handleIdBlur}
                                                        maxLength={50}
                                                        counterPosition="end"
                                                        error={idFieldError}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Platform mapping card (portal dropdown for platform selection) */}
                        <div className="info-card new-info-overflow-auto main-company-body-inner">
                            <div className="info-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <h4 className="info-title">Platform Mapping</h4>
                                <button 
                                    className={!enableTestSync ? 'disabled' : ''} 
                                    style={{ 
                                        borderRadius: '8px', 
                                        background: '#ffffff', 
                                        padding: '10px', 
                                        fontSize: '12px', 
                                        border: "1px solid #212529", 
                                        height: "34px", 
                                        minWidth: "100px", 
                                        display: "flex", 
                                        justifyContent: "center", 
                                        alignItems: "center", 
                                        gap: '10px', 
                                        fontFamily: "Montserrat", 
                                        fontSize: '12px', 
                                        fontWeight: "600", 
                                        fontStyle: "normal", 
                                        lineHeight: "normal",
                                        cursor: enableTestSync ? 'pointer' : 'not-allowed',
                                        opacity: enableTestSync ? 1 : 0.5
                                    }} 
                                    onClick={() => {
                                        if (enableTestSync) {
                                            setShowTestSyncModal(true);
                                        }
                                    }}
                                >
                                    Test Sync
                                </button>
                            </div>
                            <div className="info-content d-block entities-tab">
                                <div style={{ padding: '20px', boxShadow: '0px 1px 4px #0000001A', borderRadius: '12px' }}>
                                    <div className="table-wrapper" style={{ position: 'relative', overflowX: 'auto' }}>
                                        <table className="entities-table tab-table w-100">
                                            <thead height="35px">
                                                <tr>
                                                    <th />
                                                    <th><span>Platform <span style={{ color: "#DC3545" }}>*</span> </span></th>
                                                    <th><span>Product ID  <span style={{ color: "#DC3545" }}>*</span></span></th>
                                                    <th><span>SKU ID  <span style={{ color: "#DC3545" }}>*</span></span></th>
                                                    <th /></tr>
                                            </thead>
                                            <ReactSortable list={rows} setList={set_rows} handle=".drag-handle" tag="tbody">
                                                {rows.map((r, idx) => {
                                                    if (!row_refs.current[r.id]) row_refs.current[r.id] = React.createRef();
                                                    return (
                                                        <PlatformRow key={r.id}
                                                            ref_node={row_refs.current[r.id]}
                                                            row_state={r}
                                                            delete_row={delete_row}
                                                            update_row={update_row}
                                                            platform_options={platformOptions}
                                                            SuccessIcon={SuccessIcon}
                                                            FailedIcon={FailedIcon}
                                                            duplicateErrors={duplicateErrors}
                                                            setDuplicateErrors={setDuplicateErrors}
                                                            isFirstRow={idx === 0}
                                                            showEmptyFieldErrors={showEmptyFieldErrors && !r.isNew}
                                                        />
                                                    );
                                                })}
                                            </ReactSortable>
                                        </table>
                                    </div>

                                    <div className="edit-organisation-info" style={{ display: 'flex', marginTop: '20px', padding: '10px' }}>
                                        <button onClick={add_row} className="dark-button">Add Mapping</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="info-card">
                            <div className="info-header"><h4 className="info-title">Additional Info</h4></div>
                            <div className="info-content">
                                <div className="info-inner common-textarea">
                                    <label htmlFor="notes" className="form-label">Note</label>
                                    <textarea 
                                        className="form-control w-100 common-textarea-all" 
                                        id="notes" 
                                        rows="3" 
                                        name="notes" 
                                        value={form_data.notes} 
                                        onChange={(e) => {
                                            set_form_data({ ...form_data, notes: e.target.value });
                                            setIsDirty(true);
                                        }}
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

function PlatformRow({ ref_node, row_state: r, delete_row, update_row, platform_options, SuccessIcon, FailedIcon, duplicateErrors, setDuplicateErrors, isFirstRow, showEmptyFieldErrors }) {
    const platform_button_ref = useRef(null);
    const [portal_style, set_portal_style] = useState({});
    const [hoveredOption, setHoveredOption] = useState(null);
    const [emptyFieldErrors, setEmptyFieldErrors] = useState({
        platform: false,
        product_id: false,
        sku_id: false
    });

    useEffect(() => {
        if (r.platform_open && platform_button_ref.current) {
            const rect = platform_button_ref.current.getBoundingClientRect();
            set_portal_style({
                position: 'absolute',
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX,
                width: Math.max(280, rect.width),
                maxHeight: 270,
                overflowY: 'auto',
                zIndex: 2000,
            });
        }
    }, [r.platform_open, r.platform]);

    // Validate empty fields when showEmptyFieldErrors changes or when fields change
    useEffect(() => {
        if (showEmptyFieldErrors) {
            validateEmptyFields();
        }
    }, [r.platform, r.product_id, r.sku_id, showEmptyFieldErrors]);

    const validateEmptyFields = () => {
        const errors = {
            platform: !r.platform || r.platform.trim() === '',
            product_id: !r.product_id || r.product_id.trim() === '',
            sku_id: !r.sku_id || r.sku_id.trim() === ''
        };
        setEmptyFieldErrors(errors);
        return errors;
    };

    // Check if this row has any error that should block saving
    // Only empty fields and duplicate errors should block saving
    // Test sync failures (hasError) should NOT block saving
    const hasEmptyFieldError = emptyFieldErrors.platform || emptyFieldErrors.product_id || emptyFieldErrors.sku_id;
    const hasBlockingError = duplicateErrors?.general?.[r.id] || hasEmptyFieldError;
    const hasNonBlockingError = r.hasError && !duplicateErrors?.general?.[r.id]; // Test sync failures
    
    // Determine if we should show the FailedIcon (for test sync failures)
    const showFailedIcon = r.hasError && !duplicateErrors?.general?.[r.id];
    const showSuccessIcon = r.verified && !r.hasError && !duplicateErrors?.general?.[r.id];

    // Check if each field has a blocking error
    const hasProductIdBlockingError = duplicateErrors?.fields?.[r.id]?.productId || (showEmptyFieldErrors && emptyFieldErrors.product_id);
    const hasSkuIdBlockingError = duplicateErrors?.fields?.[r.id]?.skuId || (showEmptyFieldErrors && emptyFieldErrors.sku_id);

    // Determine error message for each field - only show if showEmptyFieldErrors is true for empty fields
    const getPlatformErrorMessage = () => {
        if (showEmptyFieldErrors && emptyFieldErrors.platform) return "Empty Fields";
        return "";
    };

    const getProductIdErrorMessage = () => {
        if (duplicateErrors?.fields?.[r.id]?.productId) return "ID already used for this platform";
        if (showEmptyFieldErrors && emptyFieldErrors.product_id) return "Empty Fields";
        return "";
    };

    const getSkuIdErrorMessage = () => {
        if (duplicateErrors?.fields?.[r.id]?.skuId) return "ID already used for this platform";
        if (showEmptyFieldErrors && emptyFieldErrors.sku_id) return "Empty Fields";
        return "";
    };

    // Get border color based on errors and showEmptyFieldErrors
    const getPlatformBorderColor = () => {
        if (showEmptyFieldErrors && emptyFieldErrors.platform) return '#DC3545';
        return '#6C757D';
    };

    const getProductIdBorderColor = () => {
        if (duplicateErrors?.fields?.[r.id]?.productId) return '#DC3545';
        if (showEmptyFieldErrors && emptyFieldErrors.product_id) return '#DC3545';
        if (r.hasError && r.product_name === 'Invalid ID') return '#DC3545';
        return '#6C757D';
    };

    const getSkuIdBorderColor = () => {
        if (duplicateErrors?.fields?.[r.id]?.skuId) return '#DC3545';
        if (showEmptyFieldErrors && emptyFieldErrors.sku_id) return '#DC3545';
        if (r.hasError && (r.sku_name === 'Invalid ID' || r.product_name === 'Invalid ID')) return '#DC3545';
        return '#6C757D';
    };

    // Update row with validation
const handleUpdateRow = (id, changes) => {
    const isFieldChange = changes.platform !== undefined || changes.product_id !== undefined || changes.sku_id !== undefined;

    // Update row - let the main update_row handle all validation
    // Also clear hasError when user edits any field
    update_row(id, {
        ...changes,
        verified: false,
        ...(isFieldChange && { hasError: false, errorMessage: null })
    });

    // Validate after update if any of the fields are being changed
    if (isFieldChange) {
        setTimeout(() => {
            const updatedRow = { ...r, ...changes };
            const errors = {
                platform: !updatedRow.platform || updatedRow.platform.trim() === '',
                product_id: !updatedRow.product_id || updatedRow.product_id.trim() === '',
                sku_id: !updatedRow.sku_id || updatedRow.sku_id.trim() === ''
            };
            setEmptyFieldErrors(errors);
        }, 0);
    }
};

    const handleDeleteClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isFirstRow) return;
        delete_row(r.id);
    };

    return (
        <tr ref={ref_node} style={{ position: (r.platform_open) ? 'relative' : 'static', zIndex: (r.platform_open) ? 1000 : 'auto' }}>
            <td style={{ width: '60px', verticalAlign: 'top', paddingTop: '12px' }}>
                <button 
                    className="btn p-0" 
                    onClick={handleDeleteClick}
                    style={{ 
                        boxShadow: 'none',
                        opacity: isFirstRow ? 0.3 : 1,
                        cursor: isFirstRow ? 'not-allowed' : 'pointer'
                    }}
                    disabled={isFirstRow}
                    title={isFirstRow ? "First row cannot be deleted" : "Delete row"}
                >
                    <Trash2 size={18} />
                </button>
            </td>

            <td style={{ position: 'relative', minWidth: '200px', maxWidth: '200px', verticalAlign: 'top', paddingTop: '8px' }}>
                <div ref={platform_button_ref} className="form-control new-form-select-cls"
                    onClick={() => handleUpdateRow(r.id, { platform_open: !r.platform_open })}
                    style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem',
                        minHeight: '40px',
                        background: 'none',
                        borderColor: getPlatformBorderColor(),
                        marginBottom: (showEmptyFieldErrors && emptyFieldErrors.platform) ? '2px' : '0'
                    }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto' }}>
                        {/* Show success/error icon based on test sync result */}
                        {showSuccessIcon && <SuccessIcon />}
                        {showFailedIcon && <FailedIcon />}
                        {r.platform || 'Select platform'}
                    </span>
                    <ChevronDown size={16} style={{ transform: r.platform_open ? 'rotate(180deg)' : 'rotate(0)' }} />
                </div>

                {/* Empty field error for platform - only show when showEmptyFieldErrors is true */}
                {showEmptyFieldErrors && emptyFieldErrors.platform && (
                    <div style={{
                        color: '#DC3545',
                        fontSize: '11px',
                        lineHeight: '14px',
                        marginTop: '2px',
                        paddingLeft: '4px'
                    }}>
                        Empty Fields
                    </div>
                )}

                {r.platform_open && (
                    <OptionPortal style={portal_style}>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {platform_options.map((opt, i) => (
                                <li
                                    key={i}
                                    onClick={() => handleUpdateRow(r.id, { platform: opt, platform_open: false })}
                                    onMouseEnter={() => setHoveredOption(i)}
                                    onMouseLeave={() => setHoveredOption(null)}
                                    style={{
                                        padding: '10px',
                                        cursor: 'pointer',
                                        borderRadius: 6,
                                        fontFamily: 'Montserrat',
                                        color: '#212529',
                                        backgroundColor: hoveredOption === i ? '#F8F9FA' : 'transparent',
                                    }}
                                >{opt}</li>
                            ))}
                        </ul>
                    </OptionPortal>
                )}
            </td>

            <td style={{ minWidth: '200px', maxWidth: '200px', position: 'relative', verticalAlign: 'top', paddingTop: '8px' }}>
                <div>
                    <input
                        id={`product_id_${r.id}`}
                        name={`product_id_${r.id}`}
                        className="form-control common-input-all"
                        value={r.product_id}
                        onChange={e => handleUpdateRow(r.id, { product_id: e.target.value })}
                        onBlur={() => {
                            if (showEmptyFieldErrors) {
                                validateEmptyFields();
                            }
                        }}
                        style={{
                            border: `1px solid ${getProductIdBorderColor()}`
                        }}
                    />
                    {/* Show error message only when appropriate */}
                    {duplicateErrors?.fields?.[r.id]?.productId && (
                        <div style={{
                            color: '#DC3545',
                            fontSize: '11px',
                            lineHeight: '14px',
                            marginTop: '2px',
                            paddingLeft: '4px'
                        }}>
                            ID already used for this platform
                        </div>
                    )}
                    {!duplicateErrors?.fields?.[r.id]?.productId && showEmptyFieldErrors && emptyFieldErrors.product_id && (
                        <div style={{
                            color: '#DC3545',
                            fontSize: '11px',
                            lineHeight: '14px',
                            marginTop: '2px',
                            paddingLeft: '4px'
                        }}>
                            Empty Fields
                        </div>
                    )}
                    {!duplicateErrors?.fields?.[r.id]?.productId && !(showEmptyFieldErrors && emptyFieldErrors.product_id) && r.hasError && r.product_name === 'Invalid ID' && (
                        <div style={{
                            color: '#DC3545',
                            fontSize: '11px',
                            lineHeight: '14px',
                            marginTop: '2px',
                            paddingLeft: '4px'
                        }}>
                            Invalid ID
                        </div>
                    )}
                </div>
            </td>

            <td style={{ minWidth: '200px', maxWidth: '200px', position: 'relative', verticalAlign: 'top', paddingTop: '8px' }}>
                <div>
                    <input
                        id={`sku_id_${r.id}`}
                        name={`sku_id_${r.id}`}
                        className="form-control common-input-all"
                        value={r.sku_id}
                        onChange={e => handleUpdateRow(r.id, { sku_id: e.target.value })}
                        onBlur={() => {
                            if (showEmptyFieldErrors) {
                                validateEmptyFields();
                            }
                        }}
                        style={{
                            border: `1px solid ${getSkuIdBorderColor()}`
                        }}
                    />
                    {/* Show error message only when appropriate */}
                    {duplicateErrors?.fields?.[r.id]?.skuId && (
                        <div style={{
                            color: '#DC3545',
                            fontSize: '11px',
                            lineHeight: '14px',
                            marginTop: '2px',
                            paddingLeft: '4px'
                        }}>
                            SKU ID already used for this platform
                        </div>
                    )}
                    {!duplicateErrors?.fields?.[r.id]?.skuId && showEmptyFieldErrors && emptyFieldErrors.sku_id && (
                        <div style={{
                            color: '#DC3545',
                            fontSize: '11px',
                            lineHeight: '14px',
                            marginTop: '2px',
                            paddingLeft: '4px'
                        }}>
                            Empty Fields
                        </div>
                    )}
                    {!duplicateErrors?.fields?.[r.id]?.skuId && !(showEmptyFieldErrors && emptyFieldErrors.sku_id) && r.hasError && (r.sku_name === 'Invalid ID' || r.product_name === 'Invalid ID') && (
                        <div style={{
                            color: '#DC3545',
                            fontSize: '11px',
                            lineHeight: '14px',
                            marginTop: '2px',
                            paddingLeft: '4px'
                        }}>
                            Invalid ID
                        </div>
                    )}
                </div>
            </td>

            <td className="drag-handle" style={{ cursor: 'grab', verticalAlign: 'top', paddingTop: '12px' }}><Menu size={18} /></td>
        </tr>
    );
}